import React, { ReactNode, useCallback, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import ExtendedModal from 'components/ExtendedModal'
import NumericInput from 'components/Input/NumericInput'
import { FlexDiv, FlexDivCentered, FlexDivCol, FlexDivRowCentered } from 'components/layout/flex'
import Pill from 'components/Pill'
import Spacer from 'components/Spacer'
import { Body } from 'components/Text'
import { IS_ONE_CLICK_TRADING_ENABLED } from 'constants/defaults'
import { EXTERNAL_LINKS } from 'constants/links'
import useWindowSize from 'hooks/useWindowSize'
import { setOpenModalWithParams } from 'state/app/reducer'
import { selectShowModalWithParams } from 'state/app/selectors'
import { ManageModalType } from 'state/futures/types'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { selectIsSubAccountMode } from 'state/wallet/selectors'
import { ExternalLink } from 'styles/common'
import media from 'styles/media'

import DelegationTradingTab from '../DelegationTrading/DelegationTradingTab'
import { DelegationSubTab } from '../DelegationTrading/types'
import DepositCrossMargin from '../DepositWithdraw/DepositCrossMargin'
import WithdrawCrossMargin from '../DepositWithdraw/WithdrawCrossMargin'
import ManageKeeperBalanceCrossMargin from '../ManageKeeperBalance/ManageKeeperBalanceCrossMargin'

import { BridgeTab } from './ManageSmartMarginModal'
import OneClickTradingTab from './OneClickTrading/OneClickTradingTab'

type Props = {
	onDismiss(): void
}

const ManageCrossMarginModal: React.FC<Props> = ({ onDismiss }) => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()

	const modalPrams = useAppSelector(selectShowModalWithParams)?.params
	const tab = modalPrams?.tab ?? ManageModalType.Deposit
	const subTab = modalPrams?.subTab ?? DelegationSubTab.Delegations
	const isSubAccountMode = useAppSelector(selectIsSubAccountMode)

	const setManageModalType = useCallback(
		(modalType: ManageModalType) =>
			dispatch(
				setOpenModalWithParams({
					type: 'futures_deposit_withdraw_cross_margin',
					params: { tab: modalType },
				})
			),
		[dispatch]
	)

	const { greaterThanWidth } = useWindowSize()

	const isDesktop = useMemo(() => greaterThanWidth('md'), [greaterThanWidth])

	const ManageMenuComponent = useMemo(() => {
		return isDesktop ? ManageMenuDesktop : ManageMenuMobile
	}, [isDesktop])

	const values: {
		title: string
		value: ManageModalType
		header?: string
		suffix?: ReactNode
		component: ReactNode
		href?: string
	}[] = useMemo(() => {
		const list = [
			{
				title: t('futures.modals.manage.deposit-title'),
				header: t('futures.modals.manage.deposit-cm-header'),
				value: ManageModalType.Deposit,
				component: <DepositCrossMargin />,
			},
			{
				title: t('futures.modals.manage.withdraw-title'),
				value: ManageModalType.Withdraw,
				header: t('futures.modals.manage.withdraw-cm-header'),
				component: <WithdrawCrossMargin />,
			},
			{
				title: t('futures.modals.manage.bridge-title'),
				value: ManageModalType.Bridge,
				header: t('futures.modals.manage.bridge-cm-header'),
				component: <BridgeTab isSubAccountMode={isSubAccountMode} />,
			},
			{
				title: t('futures.modals.manage.order.title'),
				header: t('futures.market.trade.orders.manage-keeper-deposit.header', { currency: 'USDC' }),
				value: ManageModalType.Orders,
				component: <ManageKeeperBalanceCrossMargin />,
			},
		]

		const oneclickItem = {
			title: t('futures.modals.manage.one-click-trading-title'),
			value: ManageModalType.OneClick,
			header: t('futures.modals.manage.one-click-trading-header'),
			component: <OneClickTradingTab />,
		}

		const delegationItem = {
			title: t('futures.market.trade.margin.modal.delegation.title'),
			value: ManageModalType.Delegation,
			header: t('futures.market.trade.margin.modal.delegation.copy'),
			suffix: <Pill color="lightGreen">{t('futures.modals.manage.new-function')}</Pill>,
			component: <DelegationTradingTab defaultTab={subTab || DelegationSubTab.Delegations} />,
			href: EXTERNAL_LINKS.Docs.DelegationTrading,
		}

		if (isDesktop) {
			if (IS_ONE_CLICK_TRADING_ENABLED) {
				list.push(oneclickItem)
			}
			list.push(delegationItem)
		} else {
			if (IS_ONE_CLICK_TRADING_ENABLED) {
				list.unshift(oneclickItem)
			}
			list.unshift(delegationItem)
		}

		return list
	}, [isDesktop, isSubAccountMode, subTab, t])

	const currentView = useMemo(() => {
		return values.find(({ value }) => value === tab)
	}, [tab, values])

	return (
		<StyledExtendedModal isOpen onDismiss={onDismiss}>
			<ManageContent>
				{!isDesktop && <ManageMenuBorder />}
				<ManageMenuComponent>
					{isDesktop && <ManageMenuTitle>{t('futures.modals.manage.title')}</ManageMenuTitle>}
					{values.map(({ title, suffix, value }) => (
						<ManageMenuItemButton
							key={title}
							active={value === tab}
							onClick={() => setManageModalType(value)}
						>
							{title}
							{!!suffix && <span>{suffix}</span>}
						</ManageMenuItemButton>
					))}
				</ManageMenuComponent>
				{currentView && (
					<ManageView>
						<ManageViewTitle>{currentView.title}</ManageViewTitle>
						{currentView.header && (
							<>
								<Body color="secondary">
									<Trans
										i18nKey={currentView.header}
										components={[<Emphasis href={currentView.href} />]}
									/>
								</Body>
								<Spacer height={20} />
							</>
						)}
						{currentView.component}
					</ManageView>
				)}
			</ManageContent>
		</StyledExtendedModal>
	)
}

const Emphasis = styled(ExternalLink)`
	color: ${(props) => props.theme.colors.common.primaryYellow};
	cursor: pointer;
`

const ManageMenuDesktop = styled(FlexDivCol)`
	padding: 20px;
	padding-left: 0;
	height: 100%;
	gap: 20px;
	border-right: ${(props) => props.theme.colors.selectedTheme.border};
`

const ManageMenuMobile = styled(FlexDivCentered)`
	position: absolute;
	width: calc(100% - 65px);
	overflow-x: auto;
	padding: 12.5px;
	bottom: 0;
	left: 65px;
	gap: 10px;
	height: 65px;
	background-color: ${(props) =>
		props.theme.colors.selectedTheme.newTheme.tabs.position.background};
`

const ManageMenuBorder = styled.div`
	border-top: ${(props) => props.theme.colors.selectedTheme.border};
	background-color: ${(props) =>
		props.theme.colors.selectedTheme.newTheme.tabs.position.background};
	position: absolute;
	bottom: 65px;
	left: 0;
	right: 0;
	width: 100%;
	height: 1px;
`

const ManageMenuTitle = styled.h3`
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.sectionHeader};
	text-transform: uppercase;
	font-family: ${(props) => props.theme.fonts.black};
	font-size: 13px;
	line-height: 1.2;
	margin: 0;
`

const ManageMenuItemButton = styled.button<{ active?: boolean }>`
	white-space: nowrap;
	color: ${(props) =>
		props.theme.colors.selectedTheme.input.label[props.active ? 'primary' : 'secondary']};
	transition: background-color 0.1s ease-in-out;
	background-color: transparent;
	font-size: 15px;
	display: flex;
	width: fit-content;
	align-items: center;
	justify-content: flex-start;
	line-height: 18px;
	font-weight: 700;
	padding: 8.5px 10px;
	border-radius: 100px;
	border: none;
	cursor: pointer;
	gap: 8px;

	&:hover {
		background-color: ${(props) =>
			props.theme.colors.selectedTheme.newTheme.button.default.background};
	}

	${(props) =>
		props.active &&
		css`
			background-color: ${(props) =>
				props.theme.colors.selectedTheme.newTheme.button.default.hover.background};} !important;
		`};
`

const ManageView = styled(FlexDivCol)`
	padding: 20px 0 20px 20px;

	${media.lessThan('md')`
		padding: 20px 0;
	`}
	width: 100%;
`

const ManageViewTitle = styled.h3`
	font-size: 14px;
	font-weight: 400
	line-height: 16px;
	margin: 0 0 20px 0;
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.primary};
`

export const StyledExtendedModal = styled(ExtendedModal)`
	padding: 0 20px;

	[data-reach-dialog-content] {
		margin-top: 5vh;
		width: 700px;
		height: 700px;

		${media.lessThan('md')`
			margin: 0;
			width: 100%;
			max-height: 100%;
		`}
	}
`

const ManageContent = styled(FlexDiv)`
	background-color: ${(props) =>
		props.theme.colors.selectedTheme.newTheme.tabs.position.background};
	height: 100%;
`

export const BalanceContainer = styled(FlexDivRowCentered)`
	margin-bottom: 8px;
	p {
		margin: 0;
	}
`

export const BalanceText = styled.p<{ $gold?: boolean }>`
	color: ${(props) =>
		props.$gold ? props.theme.colors.selectedTheme.yellow : props.theme.colors.selectedTheme.gray};
	span {
		color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	}
`

export const MaxButton = styled.button`
	padding: 5px 10px;
	background: ${(props) => props.theme.colors.selectedTheme.button.background};
	border-radius: 15px;
	font-family: ${(props) => props.theme.fonts.mono};
	font-size: 12px;
	line-height: 12px;
	border: ${(props) => props.theme.colors.selectedTheme.border};
	color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	cursor: pointer;
	font-variant: all-small-caps;
`

export const DepositInputMain = styled(NumericInput)`
	border: 0;
	padding: 0;
	height: 23px;
	background: transparent;
	box-shadow: none;

	input {
		font-size: 30px;
		letter-spacing: -1px;
		height: 30px;
		width: 100%;
	}
`

export const DepositInputContainer = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 15px;
	border: ${(props) => props.theme.colors.selectedTheme.newTheme.border.style};
	border-radius: 8px;

	input {
		font-size: 19px;
		font-family: ${(props) => props.theme.fonts.mono};
		color: ${(props) => props.theme.colors.selectedTheme.text.value};
		border: none;
		background-color: transparent;

		&:focus {
			outline: none;
		}
	}
`

export default ManageCrossMarginModal
